import { types as t, flow, Instance, getParent } from '@invisr/mobx-state-tree'
import crudActions from './crud-actions'
import Mapping from './mapping'
import secretsActions from './secrets-actions'
import { getNestedDataFields } from '../../utils/datasource-config-utils'
import ref, { safeReference } from './reference'
import Assignment from './assignment'
import { makeObservable, computed } from 'mobx'

const saveActions = (self: any) => ({
  onClone: flow(function* (newDbKey) {
    const secretFields = self.getSecretFields(self.mapping.children)?.filter(f => f.value);
    const data = {
      provider: 'StackContext',
      sourceCredentialsDbKey: self.dbKey,
      targetCredentialsDbKey: newDbKey,
      secrets: {
        fields: secretFields
      }
    };
    yield self.cloneSecretData(data);
  }),
  afterSave: flow(function* (before, after) {
    const secretFields = after.getSecretFields(after.mapping.children)?.filter(f => f.value);
    after.clearSecretFields(after.mapping.children);

    const data = {
      provider: 'StackContext',
      dbKey: after.dbKey,
      secrets: {
        fields: secretFields
      }
    };
    for(let a of after.envVars || []) {
      yield * a.afterSave(undefined, a);
    }
    if(data.secrets.fields.length) yield self.saveSecretData(data);
  }),
  afterCreate() {
    if (!self.inputs) {
      self.update('inputs', {
        type: 'object',
        children: []
      })
    }
  }
})

const EnvVars = t.model('EnvVars', {
  assignment: ref(Assignment, 'assignments'),
  inputs: t.maybeNull(t.late(() => Mapping)),
})
.views(self => ({
  get dbKey () {
    return getParent(getParent(self))?.dbKey + '_' + self.assignment?.id
  },
  get mapping() {
    return self.inputs
  }
}))
.actions(crudActions)
.actions(secretsActions)
.actions(saveActions)

export class StackContextProvider {
  config
  constructor(config) {
    makeObservable(this, {
      data: computed
    })
    this.config = config
  }

  get data() {
    let mapping = this.config.mapping
    if(typeof location !== 'undefined') {
      const m = location.pathname.match(/\/preview\/([^\/]+)/)
      if(m?.[1]) {
        const env = this.config.envVars.find(e => e.assignment?.id === m[1])
        mapping = env?.mapping
      }
    }
    return mapping?.toProps
  }

  query() {
    return this.data
  }
}

const StackContext = t
  .model('StackContext', {
    type: t.optional(t.string, 'StackContext'),
    inputs: t.maybeNull(t.late(() => Mapping)),
    envVars: t.optional(t.array(EnvVars), []),
  })
  .views(() => ({
    get provider() {
      return StackContextProvider
    }
  }))
  .views((self: any) => ({
    get mapping() {
      return self.inputs
    },
    labels() {
      return {
        key: self.id,
        label: self.name,
        childNodes: [
          {
            key: 'data',
            label: 'Data',
            childNodes:
              self.inputs &&
              self.inputs.children &&
              getNestedDataFields(self.inputs.children, 3),
            isExpanded: false
          },
          {
            key: 'methods',
            label: 'Methods',
            isExpanded: false,
            childNodes: [
              {
                key: 'query',
                label: 'query',
                secondaryLabel: 'FUNCTION'
              }
            ]
          }
        ]
      }
    }
  }))
  .actions(crudActions)
  .actions(secretsActions)
  .actions(saveActions)
  .actions(self => ({
    setEnvVar(assignmentId, inputs) {
      const ev = self.envVars.find(e => e.assignment?.id === assignmentId);
      if(ev) ev.update({ inputs })
      else self.envVars.push({ assignment: assignmentId, inputs })
    }
  }))

export type IStackContextDataSource = Instance<typeof StackContext>;
export default StackContext;
